import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPANY = {
  name: "Société NPDS",
  brand: "Imaginary Collection",
  siren: "534 221 387",
  siret: "534 221 387 000 28",
  vat: "FR06534221387",
  address: "67 rue la Fayette, 75009 Paris, France",
  email: "hello@thexgoeshere.com",
};

const P = ({ children }) => (
  <p className="mt-4 text-sm md:text-base font-light leading-relaxed text-[#111111]/70">{children}</p>
);
const H = ({ children }) => (
  <h2 className="mt-10 font-display text-xl md:text-2xl tracking-tight text-[#111111]">{children}</h2>
);

function WithdrawalForm({ lang }) {
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", order: "", message: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/commissions`, {
        name: form.name,
        email: form.email,
        subject: `WITHDRAWAL — order ${form.order}`,
        message: form.message,
        space_type: "withdrawal",
      });
      setSent(true);
    } catch {
      toast.error(lang === "fr" ? "Envoi impossible. Réessayez." : "Could not send. Please try again.");
    }
  };
  if (sent) {
    return (
      <p className="mt-6 border gallery-line bg-white px-6 py-5 text-sm font-light" data-testid="withdrawal-sent">
        {lang === "fr"
          ? "Votre demande de rétractation a bien été enregistrée. Nous revenons vers vous sous 48 h ouvrées."
          : "Your withdrawal request has been recorded. We will come back to you within 2 working days."}
      </p>
    );
  }
  const field =
    "w-full border border-[#111111]/25 bg-white px-4 py-3 text-sm font-light outline-none focus:border-[#111111]";
  return (
    <form onSubmit={submit} className="mt-6 max-w-xl space-y-4" data-testid="withdrawal-form">
      <input required placeholder={lang === "fr" ? "Nom complet" : "Full name"} className={field}
        value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="withdrawal-name" />
      <input required type="email" placeholder="Email" className={field}
        value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="withdrawal-email" />
      <input required placeholder={lang === "fr" ? "Référence de commande" : "Order reference"} className={field}
        value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="withdrawal-order" />
      <textarea placeholder={lang === "fr" ? "Produit(s) concerné(s)" : "Item(s) concerned"} rows={3} className={field}
        value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="withdrawal-items" />
      <button type="submit" data-testid="withdrawal-submit"
        className="bg-[#111111] px-7 py-4 text-sm font-light tracking-wide text-[#faf8f5] transition-opacity duration-500 hover:opacity-85">
        {lang === "fr" ? "Envoyer ma demande" : "Submit withdrawal"}
      </button>
    </form>
  );
}

const CONTENT = {
  "mentions-legales": {
    title: { en: "Mentions légales", fr: "Mentions légales" },
    body: (lang) => (
      <>
        <H>{lang === "fr" ? "Éditeur du site" : "Site publisher"}</H>
        <P>
          {COMPANY.name} — {COMPANY.brand}
          <br />SIREN {COMPANY.siren} · SIRET {COMPANY.siret}
          <br />{lang === "fr" ? "TVA intracommunautaire" : "EU VAT"} {COMPANY.vat}
          <br />{COMPANY.address}
          <br />{COMPANY.email}
        </P>
        <H>{lang === "fr" ? "Directeur de la publication" : "Publication director"}</H>
        <P>{COMPANY.name}.</P>
        <H>{lang === "fr" ? "Hébergement" : "Hosting"}</H>
        <P>
          {lang === "fr"
            ? "Site hébergé sur une infrastructure cloud sécurisée (conteneurs Kubernetes)."
            : "Hosted on a secure cloud infrastructure (Kubernetes containers)."}
        </P>
        <H>{lang === "fr" ? "Propriété intellectuelle" : "Intellectual property"}</H>
        <P>
          {lang === "fr"
            ? "L'ensemble des contenus du site (visuels, textes, concept « The X Goes Here ») est la propriété exclusive de Imaginary Collection. Toute reproduction sans autorisation est interdite."
            : "All site content (visuals, texts, the “The X Goes Here” concept) is the exclusive property of Imaginary Collection. Any reproduction without authorisation is prohibited."}
        </P>
      </>
    ),
  },
  cgv: {
    title: { en: "Terms of Sale — CGV", fr: "Conditions Générales de Vente" },
    body: (lang) => (
      <>
        <H>{lang === "fr" ? "1. Objet" : "1. Scope"}</H>
        <P>
          {lang === "fr"
            ? `Les présentes conditions régissent les ventes de tirages d'art en édition numérotée sur le site Imaginary Collection, édité par ${COMPANY.name}, SIREN ${COMPANY.siren}, ${COMPANY.address}.`
            : `These terms govern the sale of numbered-edition art prints on the Imaginary Collection website, published by ${COMPANY.name}, SIREN ${COMPANY.siren}, ${COMPANY.address}.`}
        </P>
        <H>{lang === "fr" ? "2. Produits" : "2. Products"}</H>
        <P>
          {lang === "fr"
            ? "Chaque tirage est produit à la demande sur papier Hahnemühle German Etching 310 g/m², en édition numérotée, avec ou sans encadrement chêne. Les visuels de mise en situation sont indicatifs."
            : "Each print is made to order on Hahnemühle German Etching 310 g/m² paper, in a numbered edition, with or without oak framing. In-situ visuals are indicative."}
        </P>
        <H>{lang === "fr" ? "3. Prix" : "3. Prices"}</H>
        <P>
          {lang === "fr"
            ? "Les prix sont indiqués en euros TTC (TVA française de 20 %). Une facture TVA est émise pour chaque commande."
            : "Prices are shown in euros, inclusive of 20% French VAT. A VAT invoice is issued for every order."}
        </P>
        <H>{lang === "fr" ? "4. Commande et paiement" : "4. Orders & payment"}</H>
        <P>
          {lang === "fr"
            ? "Le paiement s'effectue via Stripe (carte bancaire, Apple Pay, Google Pay). La commande est ferme dès confirmation du paiement. Un reçu vous est envoyé par email."
            : "Payment is processed by Stripe (card, Apple Pay, Google Pay). The order is firm once payment is confirmed. A receipt is emailed to you."}
        </P>
        <H>{lang === "fr" ? "5. Livraison" : "5. Delivery"}</H>
        <P>
          {lang === "fr"
            ? "France et Union européenne. Tirage sans cadre : 12 € (FR) / 20 € (UE). Encadré 40×50 et 50×70 : 25 € / 45 €. Encadré 70×100 : 45 € / 75 €. Livraison offerte dès 150 €. Délai : 10 à 14 jours ouvrés (impression à la demande, numérotation et signature à la main). Un email d'expédition avec suivi vous est envoyé."
            : "France and European Union. Unframed print: €12 (FR) / €20 (EU). Framed 40×50 and 50×70: €25 / €45. Framed 70×100: €45 / €75. Free shipping over €150. Delivery: 10–14 working days (printed to order, hand-numbered and signed in the studio). A dispatch email with tracking is sent."}
        </P>
        <H>{lang === "fr" ? "6. Droit de rétractation" : "6. Right of withdrawal"}</H>
        <P>
          {lang === "fr"
            ? "Conformément à l'article L221-18 du Code de la consommation, vous disposez de 14 jours après réception pour exercer votre droit de rétractation via le formulaire dédié. Exception légale : les biens confectionnés sur demande ou nettement personnalisés (œuvres sur mesure, murals) ne sont pas repris (art. L221-28)."
            : "Under article L221-18 of the French Consumer Code, you have 14 days from receipt to withdraw via the dedicated form. Legal exception: goods made to order or clearly personalised (custom murals) cannot be returned (art. L221-28)."}
        </P>
        <H>{lang === "fr" ? "7. Litiges" : "7. Disputes"}</H>
        <P>
          {lang === "fr"
            ? "Droit français applicable. Médiation de la consommation : vous pouvez recourir gratuitement à un médiateur en cas de litige non résolu."
            : "French law applies. Consumer mediation: you may refer an unresolved dispute to a mediator free of charge."}
        </P>
      </>
    ),
  },
  privacy: {
    title: { en: "Privacy Policy — RGPD", fr: "Politique de confidentialité — RGPD" },
    body: (lang) => (
      <>
        <H>{lang === "fr" ? "Responsable de traitement" : "Data controller"}</H>
        <P>{COMPANY.name}, {COMPANY.address} — {COMPANY.email}.</P>
        <H>{lang === "fr" ? "Données collectées" : "Data we collect"}</H>
        <P>
          {lang === "fr"
            ? "Commande : nom, adresse, email, téléphone (livraison et facturation, via Stripe). Newsletter : email, avec votre consentement. Demandes de commission : informations de votre projet. Aucune donnée bancaire n'est stockée sur nos serveurs."
            : "Orders: name, address, email, phone (delivery and invoicing, via Stripe). Newsletter: email, with your consent. Commission enquiries: your project details. No card data is ever stored on our servers."}
        </P>
        <H>{lang === "fr" ? "Vos droits" : "Your rights"}</H>
        <P>
          {lang === "fr"
            ? `Conformément au RGPD, vous disposez d'un droit d'accès, de rectification, d'effacement, de portabilité et d'opposition. Écrivez à ${COMPANY.email}. Vous pouvez saisir la CNIL (cnil.fr) en cas de litige.`
            : `Under the GDPR you have the right to access, rectify, erase, port and object to your data. Write to ${COMPANY.email}. You may lodge a complaint with the CNIL (cnil.fr).`}
        </P>
        <H>{lang === "fr" ? "Conservation" : "Retention"}</H>
        <P>
          {lang === "fr"
            ? "Données de commande : durée légale comptable (10 ans). Newsletter : jusqu'à désinscription."
            : "Order data: statutory accounting period (10 years). Newsletter: until you unsubscribe."}
        </P>
      </>
    ),
  },
  cookies: {
    title: { en: "Cookie Policy", fr: "Politique cookies" },
    body: (lang) => (
      <>
        <H>{lang === "fr" ? "Cookies essentiels" : "Essential cookies"}</H>
        <P>
          {lang === "fr"
            ? "Panier et préférence de langue (stockage local). Indispensables au fonctionnement du site, toujours actifs."
            : "Cart and language preference (local storage). Required for the site to work, always on."}
        </P>
        <H>{lang === "fr" ? "Cookies non essentiels" : "Non-essential cookies"}</H>
        <P>
          {lang === "fr"
            ? "Mesure d'audience et pixels publicitaires. Désactivés par défaut et déposés uniquement avec votre consentement via le bandeau cookies."
            : "Analytics and advertising pixels. Off by default and only set with your consent via the cookie banner."}
        </P>
        <P>
          {lang === "fr"
            ? "Vous pouvez modifier votre choix à tout moment en supprimant le stockage local de votre navigateur."
            : "You can change your choice at any time by clearing your browser's local storage."}
        </P>
      </>
    ),
  },
  "shipping-returns": {
    title: { en: "Shipping & Returns", fr: "Livraison & retours" },
    body: (lang) => (
      <>
        <H>{lang === "fr" ? "Livraison" : "Shipping"}</H>
        <P>
          {lang === "fr"
            ? "France et Union européenne uniquement. Sans cadre (tube) : 12 € France / 20 € UE. Encadré 40×50 et 50×70 : 25 € / 45 €. Encadré 70×100 : 45 € / 75 € (transporteur spécialisé). Livraison offerte dès 150 € d'achat. Délai : 10 à 14 jours ouvrés, email d'expédition avec suivi."
            : "France and European Union only. Unframed (tube): €12 France / €20 EU. Framed 40×50 and 50×70: €25 / €45. Framed 70×100: €45 / €75 (specialist carrier). Free shipping over €150. 10–14 working days, dispatch email with tracking."}
        </P>
        <H>{lang === "fr" ? "Retours" : "Returns"}</H>
        <P>
          {lang === "fr"
            ? "14 jours après réception pour changer d'avis (hors œuvres personnalisées). Le tirage doit être retourné dans son emballage d'origine ; le remboursement intervient sous 14 jours après réception du retour."
            : "14 days from receipt to change your mind (excluding personalised works). The print must be returned in its original packaging; refund within 14 days of receiving the return."}
        </P>
      </>
    ),
  },
  withdrawal: {
    title: { en: "Withdrawal Form", fr: "Formulaire de rétractation" },
    body: (lang) => (
      <>
        <P>
          {lang === "fr"
            ? "Vous disposez de 14 jours à compter de la réception pour vous rétracter (art. L221-18 du Code de la consommation), hors œuvres confectionnées sur mesure. Complétez le formulaire ci-dessous :"
            : "You have 14 days from receipt to withdraw (art. L221-18 of the French Consumer Code), excluding made-to-order works. Complete the form below:"}
        </P>
        <WithdrawalForm lang={lang} />
      </>
    ),
  },
};

export default function Legal() {
  const { page } = useParams();
  const { lang } = useLanguage();
  const entry = CONTENT[page];

  if (!entry) {
    return (
      <div className="bg-[#faf8f5] min-h-screen">
        <Nav solid />
        <div className="pt-40 text-center font-light text-[#111111]/70">Page introuvable.</div>
      </div>
    );
  }

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-28 md:pt-36 pb-24">
        <div className="mx-auto max-w-[820px] px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
            Imaginary Collection — {COMPANY.name}
          </span>
          <h1 className="mt-6 font-display text-3xl md:text-5xl tracking-tight text-[#111111]">
            {entry.title[lang] || entry.title.en}
          </h1>
          <div className="mt-4" data-testid={`legal-${page}`}>{entry.body(lang)}</div>
          <p className="mt-12 text-xs font-light text-[#111111]/40">
            <Link to="/" className="link-underline">Imaginary Collection</Link> — Paris
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
